import { useCallback, useEffect, useRef } from 'react';
import { useAtom, PrimitiveAtom } from 'jotai';
import { QueryRequest } from '../typings/request';
import useRefresh, { UseRefreshArgs } from './useRefresh';

type WithInitialValue<Value> = {
  init: Value;
};

type QueryRequestParams<T> = {
  requestAtom: PrimitiveAtom<QueryRequest<T>> & WithInitialValue<QueryRequest<T>>;
  queryFunction?: (...args: any) => Promise<T>;
  refreshEventType?: UseRefreshArgs['refreshEventType'];
  cacheData?: boolean;
  autoFetch?: boolean;
  throwErrors?: boolean;
};

export default function useQuery<T>({
  requestAtom,
  queryFunction,
  refreshEventType,
  cacheData,
  autoFetch = true,
  throwErrors = false,
}: QueryRequestParams<T>) {
  const [requestData, setRequestData] = useAtom(requestAtom);
  const dataRef = useRef(requestData?.data);
  dataRef.current = requestData?.data;
  const loadingRef = useRef(false);

  const setData = useCallback(
    (data: T) => {
      setRequestData({
        ...requestData,
        data,
      });
    },
    [requestData, setRequestData]
  );

  const fetchData = useCallback(
    async (refresh?: boolean) => {
      if (loadingRef.current) {
        return;
      }

      if (typeof queryFunction !== 'function') {
        return;
      }

      if (refresh) {
        setRequestData((prev) => {
          return {
            ...prev,
            loading: false,
            refreshing: true,
            error: undefined,
          };
        });
      }

      if (!refresh && cacheData && dataRef.current) {
        return;
      }

      loadingRef.current = true;
      return queryFunction()
        .then((data) => {
          loadingRef.current = false;
          setRequestData({
            loading: false,
            refreshing: false,
            error: undefined,
            data,
          });
        })
        .catch((error) => {
          loadingRef.current = false;
          console.error(error);
          setRequestData({ loading: false, refreshing: false, error });
          if (throwErrors) {
            throw error;
          }
        });
    },
    [queryFunction, setRequestData, cacheData, throwErrors]
  );

  const refreshData = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  useRefresh({ refreshEventType, onRefresh: refreshData });

  useEffect(() => {
    if (autoFetch) fetchData();
  }, [fetchData, autoFetch]);

  return { refresh: refreshData, requestData, setData, fetchData };
}
